import React, { useEffect, useId, useRef, useState } from "react";
import "./css/customselect.css";

function CustomSelect({
  label,
  options = [],
  selectedValue,
  onChange,
  labelExtractor,
  valueExtractor,
  searchable = false,
  searchPlaceholder = "Buscar…",
  placeholder = "Seleccionar",
  disabled = false,
  className = "",
  iosArrow = false,
  disabledExtractor,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapperRef = useRef(null);
  const searchRef = useRef(null);
  const triggerRef = useRef(null);
  const optionRefs = useRef([]);
  const labelId = useId();
  const listboxId = useId();

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    if (searchable) window.requestAnimationFrame(() => searchRef.current?.focus());
    const closeOnEscape = (event) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      window.requestAnimationFrame(() => triggerRef.current?.focus());
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [open, searchable]);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const getLabel = (item) => (labelExtractor ? labelExtractor(item) : item);
  const getValue = (item) => (valueExtractor ? valueExtractor(item) : item);
  const selectedOption = options.find((option) => getValue(option) === selectedValue);
  const selectedLabel = selectedOption ? getLabel(selectedOption) : (selectedValue || placeholder);
  const normalizedSearch = search.trim().toLocaleLowerCase("es");
  const filteredOptions = searchable && normalizedSearch
    ? options.filter((option) => String(getLabel(option)).toLocaleLowerCase("es").includes(normalizedSearch))
    : options;

  const focusOption = (index) => {
    if (!filteredOptions.length) return;
    let boundedIndex = Math.max(0, Math.min(index, filteredOptions.length - 1));
    const direction = index < optionRefs.current.indexOf(document.activeElement) || boundedIndex === filteredOptions.length - 1 ? -1 : 1;
    while (disabledExtractor?.(filteredOptions[boundedIndex])) {
      boundedIndex += direction;
      if (boundedIndex < 0 || boundedIndex >= filteredOptions.length) return;
    }
    optionRefs.current[boundedIndex]?.focus();
  };

  const handleTriggerKeyDown = (event) => {
    if (disabled || (event.key !== "ArrowDown" && event.key !== "ArrowUp")) return;
    event.preventDefault();
    setOpen(true);
    if (searchable) return;
    const selectedIndex = filteredOptions.findIndex((option) => getValue(option) === selectedValue);
    const fallbackIndex = event.key === "ArrowUp" ? filteredOptions.length - 1 : 0;
    window.requestAnimationFrame(() => focusOption(selectedIndex >= 0 ? selectedIndex : fallbackIndex));
  };

  const handleOptionKeyDown = (event, index) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      focusOption(index + 1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      focusOption(index - 1);
    } else if (event.key === "Home") {
      event.preventDefault();
      focusOption(0);
    } else if (event.key === "End") {
      event.preventDefault();
      focusOption(filteredOptions.length - 1);
    }
  };

  const choose = (option) => {
    if (disabledExtractor?.(option)) return;
    onChange(getValue(option));
    setOpen(false);
    setSearch("");
    window.requestAnimationFrame(() => triggerRef.current?.focus());
  };

  return (
    <div
      className={`custom-select-wrapper${open ? " is-open" : ""}${disabled ? " is-disabled" : ""}${className ? ` ${className}` : ""}`}
      ref={wrapperRef}
    >
      <span className="custom-select-label" id={labelId}>{label}</span>
      <div className="custom-select">
        <button
          ref={triggerRef}
          type="button"
          className="custom-select-trigger"
          onClick={() => {
            if (disabled) return;
            setOpen((current) => !current);
            if (open) setSearch("");
          }}
          onKeyDown={handleTriggerKeyDown}
          aria-labelledby={labelId}
          aria-controls={listboxId}
          aria-expanded={open}
          aria-haspopup="listbox"
          disabled={disabled}
        >
          <span className={`custom-select-selected${selectedOption ? "" : " is-placeholder"}`}>{selectedLabel}</span>
          <span className={`material-symbols-outlined custom-select-arrow${iosArrow ? " is-ios" : ""}`} aria-hidden="true">
            {iosArrow ? "expand_more" : open ? "keyboard_arrow_up" : "keyboard_arrow_down"}
          </span>
        </button>
        {open && (
          <div className="custom-select-options" id={listboxId} role="listbox" aria-labelledby={labelId}>
            {searchable && (
              <div className="custom-select-search">
                <span className="material-symbols-outlined" aria-hidden="true">search</span>
                <input
                  ref={searchRef}
                  type="search"
                  value={search}
                  placeholder={searchPlaceholder}
                  aria-label={searchPlaceholder}
                  onChange={(event) => setSearch(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "ArrowDown") {
                      event.preventDefault();
                      focusOption(0);
                    }
                  }}
                />
              </div>
            )}
            {filteredOptions.map((option, index) => {
              const selected = getValue(option) === selectedValue;
              return (
                <button
                  ref={(node) => { optionRefs.current[index] = node; }}
                  type="button"
                  key={getValue(option)}
                  className="custom-select-option"
                  role="option"
                  aria-selected={selected}
                  disabled={Boolean(disabledExtractor?.(option))}
                  onKeyDown={(event) => handleOptionKeyDown(event, index)}
                  onClick={() => choose(option)}
                >
                  <span>{getLabel(option)}</span>
                  {selected ? <span className="material-symbols-outlined custom-select-check" aria-hidden="true">check</span> : null}
                </button>
              );
            })}
            {!filteredOptions.length && <p className="custom-select-empty">Sin resultados</p>}
          </div>
        )}
      </div>
    </div>
  );
}

export default CustomSelect;
